import React, { useMemo, useState } from 'react'
import { Container, Row, Col, Card, Form, FloatingLabel, Alert, Spinner } from 'react-bootstrap'
import { 
  BsGeoAltFill, 
  BsEnvelopeFill, 
  BsTelephoneFill, 
  BsLinkedin, 
  BsGithub, 
  BsTwitter 
} from 'react-icons/bs'
import { v4 as uuidv4 } from 'uuid'
import './ContactSection.css'

const ContactSection = () => {
  const initialFormData = {
    name: '',
    email: '',
    source: 'Google Search',
    comments: '',
  }

  const [formData, setFormData] = useState(initialFormData)
  const [submitting, setSubmitting] = useState(false)
  const [submitState, setSubmitState] = useState({ variant: 'success', message: '' })
  const [copiedKey, setCopiedKey] = useState(null)

  const isEmailValid = useMemo(() => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(formData.email), [formData.email])
  const isNameValid = useMemo(() => formData.name.trim().length >= 2, [formData.name])
  const isFormValid = isEmailValid && isNameValid

  const handleInputChange = event => {
    const { name, value } = event.target
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: value
    }))
  }

  const handleCopy = (text, key) => {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(text)
      setCopiedKey(key)
      setTimeout(() => setCopiedKey(null), 2000)
    }
  }

  const handleSubmit = async event => {
    event.preventDefault()
    if (!isFormValid) {
      setSubmitState({ variant: 'danger', message: 'Please provide a valid name and email.' })
      return
    }
    setSubmitting(true)
    setSubmitState({ variant: 'success', message: '' })

    const contactEndpoint = process.env.REACT_APP_CONTACT_API_URL

    try {
      if (contactEndpoint) {
        const response = await fetch(contactEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: uuidv4(),
            timestamp: new Date().toISOString(),
            ...formData
          }),
        })

        if (!response.ok) {
          throw new Error(`Server returned status ${response.status}`)
        }
      } else {
        // Fallback when no external backend endpoint is configured
        console.log('Contact message logged (no REACT_APP_CONTACT_API_URL configured):', formData)
        await new Promise(resolve => setTimeout(resolve, 400))
      }

      setSubmitState({ variant: 'success', message: 'Thank you! Your message was sent successfully.' })
      resetForm()
    } catch (error) {
      console.error('Error sending message:', error)
      setSubmitState({ variant: 'danger', message: 'Sorry, something went wrong. Please reach out directly via email at nithin.dasyam@gmail.com.' })
    }
    setSubmitting(false)
  }

  const resetForm = () => {
    setFormData(initialFormData)
  }

  return (
    <div className="section-container">
      <Container>
        <h2 className="section-title">Get In Touch</h2>
        <Row className="g-4">
          <Col lg={5}>
            <Card className="modern-card contact-card h-100">
              <Card.Body>
                <h3 className="section-subtitle">Let's Connect</h3>
                <p className="mb-4 text-muted">
                  Have a project inquiry, architecture question, or want to discuss enterprise tech? 
                  Feel free to reach out anytime!
                </p>

                {/* Interactive Contact Items with Copy Support */}
                <div className="contact-list">
                  <div className="contact-item-link" role="button" tabIndex={0}>
                    <div className="contact-item-left">
                      <span className="contact-item-icon"><BsGeoAltFill /></span>
                      <div className="contact-item-text">
                        <span className="contact-item-label">Location</span>
                        <span className="contact-item-value">Dallas, Texas, USA</span>
                      </div>
                    </div>
                  </div>

                  <div 
                    className="contact-item-link" 
                    role="button" 
                    tabIndex={0}
                    onClick={() => handleCopy('nithin.dasyam@gmail.com', 'email')}
                    title="Click to copy email"
                  >
                    <div className="contact-item-left">
                      <span className="contact-item-icon"><BsEnvelopeFill /></span>
                      <div className="contact-item-text">
                        <span className="contact-item-label">Email</span>
                        <span className="contact-item-value">nithin.dasyam@gmail.com</span>
                      </div>
                    </div>
                    <span className={`copy-badge ${copiedKey === 'email' ? 'copied' : ''}`}>
                      {copiedKey === 'email' ? 'Copied! ✓' : 'Copy'}
                    </span>
                  </div>

                  <div 
                    className="contact-item-link" 
                    role="button" 
                    tabIndex={0}
                    onClick={() => handleCopy('+18138424187', 'phone')}
                    title="Click to copy phone number"
                  >
                    <div className="contact-item-left">
                      <span className="contact-item-icon"><BsTelephoneFill /></span>
                      <div className="contact-item-text">
                        <span className="contact-item-label">Phone</span>
                        <span className="contact-item-value">+1 (813) 842-4187</span>
                      </div>
                    </div>
                    <span className={`copy-badge ${copiedKey === 'phone' ? 'copied' : ''}`}>
                      {copiedKey === 'phone' ? 'Copied! ✓' : 'Copy'}
                    </span>
                  </div>
                </div>

                <div className="social-connect-pills">
                  <a
                    href="https://www.linkedin.com/in/nithin-dasyam"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-pill"
                    aria-label="LinkedIn profile"
                  >
                    <BsLinkedin /> LinkedIn
                  </a>
                  <a
                    href="https://github.com/nithindasyam"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-pill"
                    aria-label="GitHub profile"
                  >
                    <BsGithub /> GitHub
                  </a>
                  <a
                    href="https://twitter.com/nithin_dasyam"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-pill"
                    aria-label="Twitter profile"
                  >
                    <BsTwitter /> Twitter
                  </a>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={7}>
            <Card className="modern-card contact-card h-100">
              <Card.Body>
                <h3 className="section-subtitle">Send a Message</h3>
                {submitState.message && (
                  <Alert variant={submitState.variant} className='mb-4'>
                    {submitState.message}
                  </Alert>
                )}
                <Form onSubmit={handleSubmit} noValidate>
                  <FloatingLabel controlId='name' label='Full Name' className='mb-3'>
                    <Form.Control
                      type='text'
                      name='name'
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder='John Doe'
                      isInvalid={formData.name.length > 0 && !isNameValid}
                      required
                    />
                    <Form.Control.Feedback type='invalid'>
                      Please enter at least 2 characters.
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <FloatingLabel controlId='email' label='Email Address' className='mb-3'>
                    <Form.Control
                      type='email'
                      name='email'
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder='name@example.com'
                      isInvalid={formData.email.length > 0 && !isEmailValid}
                      required
                    />
                    <Form.Control.Feedback type='invalid'>
                      Please provide a valid email address.
                    </Form.Control.Feedback>
                  </FloatingLabel>

                  <Form.Group controlId="source" className="mb-3">
                    <Form.Label className="form-label-modern">How did you find me?</Form.Label>
                    <div className="toggle-button-group" role="radiogroup" aria-label="How did you find me?">
                      {['Google Search', 'LinkedIn', 'Word of Mouth', 'Other'].map(option => (
                        <button
                          key={option}
                          id={`source-${option.replace(/\s+/g, '-').toLowerCase()}`}
                          type="button"
                          className={`toggle-btn ${formData.source === option ? 'active' : ''}`}
                          name="source"
                          value={option}
                          onClick={handleInputChange}
                          aria-pressed={formData.source === option}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </Form.Group>

                  <FloatingLabel controlId='comments' label='Message' className='mb-4'>
                    <Form.Control
                      as='textarea'
                      rows={4}
                      style={{ height: '120px' }}
                      name='comments'
                      value={formData.comments}
                      onChange={handleInputChange}
                      placeholder='Your message'
                    />
                  </FloatingLabel>

                  <div className="d-grid">
                    <button 
                      type="submit" 
                      className="modern-btn" 
                      disabled={submitting || !isFormValid}
                      aria-disabled={!isFormValid}
                    >
                      {submitting ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2"/>
                          Sending...
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default ContactSection
